import { Tabs as TabsPrimitive } from 'radix-ui';
import * as React from 'react';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

/**
 * Pill-style tabs — for switching between views of the SAME subject (e.g.
 * "By employee / By customer"), never for page-to-page navigation. One or
 * two word labels; more crowds the track. Never nest a `<PillTabs>` inside
 * another tab's content.
 *
 * State management, keyboard nav (arrow keys move between triggers),
 * `data-state="active"`, and accessibility all come from Radix — that part
 * isn't reimplemented here. The one custom piece is the sliding pill
 * indicator: `PillTabsList` measures the active trigger's `offsetLeft` /
 * `offsetWidth` (re-measured on resize via `ResizeObserver` and on tab
 * switch via `MutationObserver` watching `data-state`) and animates a
 * background div under it with a CSS transform transition.
 *
 * `Tabs` (Root) and `TabsContent` need no customization for this look, so
 * they're re-exported as-is from `components/ui/tabs` rather than
 * reimplemented — and `components/ui/tabs.tsx` itself stays untouched,
 * shadcn-managed (G9); this wrapper is where the custom behavior lives.
 * Leave `value`/`defaultValue` off the root to get Radix's own default —
 * the first tab — rather than hardcoding which one that is.
 *
 * Usage:
 *   <PillTabs defaultValue="employee">
 *     <PillTabsList>
 *       <PillTabsTrigger value="employee">Employee</PillTabsTrigger>
 *       <PillTabsTrigger value="customer">Customer</PillTabsTrigger>
 *     </PillTabsList>
 *     <PillTabsContent value="employee">...</PillTabsContent>
 *     <PillTabsContent value="customer">...</PillTabsContent>
 *   </PillTabs>
 */
interface IndicatorRect {
  left: number;
  width: number;
}

function PillTabsList({
  className,
  children,
  fullWidth = false,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> & {
  /**
   * Default (`false`): the track hugs its content (`w-fit`) — right for a
   * compact 2-3 option switch, where the indicator width should follow each
   * label.
   *
   * `true`: the track fills its container (`w-full`) and every trigger gets
   * `flex-1` (Tailwind's `flex-1` sets `flex-basis: 0%`, so the equal split
   * comes from `flex-grow` alone, ignoring each label's own width) — every
   * tab, and so the indicator under whichever is active, ends up the same
   * width. Right for a wide row of many tabs, where a "Details" pill
   * visibly smaller than an "Engagements" pill next to it reads as
   * inconsistent rather than as the labels just being different lengths.
   */
  fullWidth?: boolean;
}) {
  const listRef = React.useRef<HTMLDivElement | null>(null);
  const [indicator, setIndicator] = React.useState<IndicatorRect | null>(null);
  // Suppresses the transition on first paint — without this, the indicator
  // visibly slides in from the track's top-left corner the moment the tabs
  // mount, instead of simply appearing under whichever tab starts active.
  const [ready, setReady] = React.useState(false);

  const measure = React.useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    const active = list.querySelector<HTMLElement>('[data-state="active"]');
    if (!active) {
      setIndicator((prev) => (prev === null ? prev : null));
      return;
    }
    const next = { left: active.offsetLeft, width: active.offsetWidth };
    setIndicator((prev) =>
      prev && prev.left === next.left && prev.width === next.width ? prev : next,
    );
    setReady((prev) => (prev ? prev : true));
  }, []);

  React.useEffect(() => {
    measure();
    const list = listRef.current;
    if (!list) return;

    // Two different triggers for the same re-measure: a size change (a
    // trigger's label re-flowing, a font swapping in, the track itself
    // resizing) and an active-tab swap. Neither observer alone covers both.
    const resizeObserver = new ResizeObserver(() => measure());
    resizeObserver.observe(list);
    for (const el of list.querySelectorAll('[data-state]')) resizeObserver.observe(el);

    const mutationObserver = new MutationObserver(() => measure());
    mutationObserver.observe(list, {
      attributes: true,
      attributeFilter: ['data-state'],
      subtree: true,
    });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [measure, children]);

  return (
    <TabsPrimitive.List
      ref={listRef}
      data-slot="pill-tabs-list"
      className={cn(
        // `w-fit`: without it, this stretches to the full width of its
        // flex-column parent (`Tabs` Root is `flex flex-col`, whose default
        // `align-items: stretch` applies to every child) — the track then
        // reads as an oversized bar with all the triggers bunched in the
        // center via `justify-center`, instead of a pill hugging its content.
        // `fullWidth` opts back into that stretch on purpose, paired with
        // `flex-1` on every trigger so the extra space is divided evenly
        // instead of left empty on both sides.
        //
        // `shrink-0`: `Tabs` Root is also `flex flex-col`, so this List is
        // itself a flex item on the vertical axis — `flex-shrink` defaults to
        // 1 for every flex item regardless of an explicit `h-10`, so once the
        // active panel's content (e.g. a long Audit log) pushed the column
        // past its constrained height, the browser was shrinking the pill
        // track itself to make room — 40px became ~30px, reading as
        // "the padding got smaller" on whichever tab had the tallest panel.
        // A fixed-height tab bar should never be the thing that gives.
        'relative inline-flex h-10 shrink-0 items-stretch justify-center rounded-full border border-border bg-surface-subtle p-1 text-muted-foreground',
        fullWidth ? 'w-full [&>[data-slot="pill-tabs-trigger"]]:flex-1' : 'w-fit',
        className,
      )}
      {...props}
    >
      {indicator ? (
        <div
          aria-hidden
          data-slot="pill-tabs-indicator"
          className={cn(
            'pointer-events-none absolute inset-y-1 left-0 rounded-full bg-card shadow-sm',
            ready && 'transition-[transform,width] duration-300 ease-out',
          )}
          style={{ width: indicator.width, transform: `translateX(${indicator.left}px)` }}
        />
      ) : null}
      {children}
    </TabsPrimitive.List>
  );
}

function PillTabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="pill-tabs-trigger"
      className={cn(
        "relative z-10 inline-flex items-center justify-center gap-1.5 rounded-full px-3 text-sm font-normal whitespace-nowrap text-muted-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:font-medium data-[state=active]:text-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  );
}

export { PillTabsList, PillTabsTrigger, Tabs as PillTabs, TabsContent as PillTabsContent };
